/**
 * The pack walkthrough's text types itself in, one row at a time. A ready team card is shown at once:
 * replaying a greeting must not make an already prepared offer look as though it is still loading.
 *
 * A pack accepted from Business Manager forwards into a fresh chat (`lib/pack-handoff`) whose first
 * turn is the Maestro's walkthrough. That turn is usually finished by the time the history read lands,
 * so it arrived as rows with no `animate` flag, and the greeting, the team card and the question all
 * appeared in one frame. Reported from a recording of the BM flow as "everything appears at once".
 *
 * The rules here are pure; `lib/use-pack-arrival-reveal` binds them to React and `MessageRow` draws
 * the reveal:
 *  - A row is part of the arrival when it is the agent's, and it was written after the accept.
 *    Sticky: once a row is staged it stays staged, because flipping `useTypewriter`'s skip flag
 *    mid-reveal freezes the text where it stands (see `.claude/rules/orion-web-chat.md`).
 *  - Staged text rows reveal in order. Team-card rows bypass that sequence, including when their
 *    metadata arrives after the row was already staged. One exception: a team card waits for the site
 *    insights row above it (`entryInsights`, lib/entry-insights-order) until that row has typed, so a
 *    pack link reads greeting, then insights, then the card (lib/pack-arrival-message).
 *  - The reader's own message ends the wait at once: holding the agent's older rows back would let
 *    the reader's reply sit above words it answers. Any other row that is not staged is drawn as it
 *    comes and holds nothing up.
 *
 * Web-only for now: see "Tracked divergence — the pack walkthrough types itself in" in the package CLAUDE.md.
 */

/** How far before the handoff armed a row may be written and still count as the walkthrough. */
export const PACK_ARRIVAL_WINDOW_MS = 2 * 60 * 1000;

/** The pause between a card fading in and the words under it starting to type. */
export const PACK_REVEAL_CARD_BEAT_MS = 450;

/** The pause after a row has finished typing, before the next one starts. */
export const PACK_REVEAL_ROW_BEAT_MS = 300;

/** One typewriter tick, the same frame step `useTypewriter` runs at. */
export const PACK_REVEAL_TICK_MS = 33;

/** The longest a row may take to type: a long walkthrough types faster rather than longer. */
const TARGET_TYPE_MS = 3000;

/** The slowest pace, in characters per tick (~90 a second): slow enough to read as typing. */
const MIN_STEP = 3;

export interface PackArrivalRow {
  id?: string;
  role?: string;
  createdDate?: string;
  streamOpen?: boolean;
  bandOfferCard?: unknown;
  bandCard?: unknown;
  bandCreated?: unknown;
  entryInsights?: boolean;
}

function hasTeamCard(row: PackArrivalRow): boolean {
  // bandCreated records the action but renders no card, so its prose still follows the text queue.
  return Boolean(row.bandOfferCard || row.bandCard);
}

/** Whether this row is the agent's walkthrough of the pack, given when the handoff armed. */
export function isPackArrivalRow(row: PackArrivalRow, armedAt: number): boolean {
  if (!row.id) return false;
  if (row.role !== 'ASSISTANT' && row.role !== 'ASSISTANT_STEP') return false;
  if (hasTeamCard(row)) return false;
  // A reply this browser is streaming already types as it arrives, and between two deltas it can look
  // finished, which would release the rows behind it early.
  if (row.streamOpen) return false;
  // A pushed row with no server date is new by definition.
  if (!row.createdDate) return true;
  const written = Date.parse(row.createdDate);
  if (Number.isNaN(written)) return false;
  return written >= armedAt - PACK_ARRIVAL_WINDOW_MS;
}

type RevealRow = PackArrivalRow;

function activeIndex(rows: ReadonlyArray<RevealRow>, staged: ReadonlySet<string>, revealed: ReadonlySet<string>): number {
  return rows.findIndex((r) => !!r.id && staged.has(r.id) && !revealed.has(r.id));
}

/**
 * The ids to hold back, for the rows in display order: the staged rows after the one typing. Nothing is
 * held once the reader has written after it. A team card is held only behind a site insights row that
 * has not typed yet — see the file comment.
 */
export function heldRowIds(rows: ReadonlyArray<RevealRow>, staged: ReadonlySet<string>, revealed: ReadonlySet<string>): Set<string> {
  const held = new Set<string>();
  if (readerInterrupted(rows, staged, revealed)) return held;
  const active = activeIndex(rows, staged, revealed);
  if (active < 0) return held;
  let insightsTyping = false;
  for (const r of rows.slice(active)) {
    if (r.id && staged.has(r.id) && !revealed.has(r.id) && r.entryInsights) insightsTyping = true;
    if (!r.id || r === rows[active]) continue;
    if (hasTeamCard(r) ? insightsTyping : staged.has(r.id)) held.add(r.id);
  }
  return held;
}

/**
 * Whether the reader has written after the row being typed. The walkthrough then completes at once:
 * every staged row is treated as revealed and shows its full words.
 */
export function readerInterrupted(rows: ReadonlyArray<RevealRow>, staged: ReadonlySet<string>, revealed: ReadonlySet<string>): boolean {
  const active = activeIndex(rows, staged, revealed);
  return active >= 0 && rows.slice(active + 1).some((r) => r.role === 'USER');
}

/** How many characters a staged row types per tick, so a row takes at most ~3s. */
export function packRevealStep(length: number): number {
  return Math.max(MIN_STEP, Math.ceil(length / (TARGET_TYPE_MS / PACK_REVEAL_TICK_MS)));
}

/**
 * The longest a row may hold the rows behind it. A safety net, not the pace: a row that never reports
 * it has finished (a run of agent cards draws only its first row) must not hold the walkthrough forever.
 */
export function packRevealFallbackMs(length: number): number {
  const ticks = Math.ceil(length / packRevealStep(length));
  return PACK_REVEAL_CARD_BEAT_MS + ticks * PACK_REVEAL_TICK_MS + 3000;
}
